import { Link, useLocation } from "wouter";
import { APP_VERSION } from "@/lib/version";
import {
  Shield,
  Users,
  LayoutDashboard,
  AlertTriangle,
  Activity,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  FileSearch,
  Trophy,
  Sprout,
  DollarSign,
  LayoutTemplate,
  Database, Ticket } from "lucide-react";
import { cn } from "@/lib/utils";

interface NavItem {
  icon: React.ElementType;
  label: string;
  href: string;
  disabled?: boolean;
}

const INTELLIGENCE_ITEMS: NavItem[] = [
  { icon: Database, label: "Meta Intelligence", href: "/admin/meta-intel" },
  { icon: FileSearch, label: "Competitors", href: "/admin/competitors" },
  { icon: Trophy, label: "Winning Ads", href: "/admin/winning-ads" },
  { icon: Sprout, label: "Ad Library Seeder", href: "/admin/ad-library-seeder" },
  { icon: Users, label: "Talent Library", href: "/admin/talent-library" },
  { icon: LayoutTemplate, label: "Ad Templates", href: "/admin/ad-templates" },
  { icon: FileSearch, label: "Platform Docs", href: "/admin/platform-docs" },
];

const BILLING_ITEMS: NavItem[] = [
  { icon: DollarSign, label: "Pricing", href: "/admin/pricing" },
  { icon: Ticket, label: "Promo Coupons", href: "/admin/coupons" },
];

const DATA_ITEMS: NavItem[] = [
  { icon: Shield, label: "Connected Accounts", href: "/admin/accounts" },
  { icon: Users, label: "User Management", href: "/admin/users" },
];

const SECTIONS = [
  { label: "Operations", items: [
    { icon: LayoutDashboard, label: "Dashboard", href: "/admin/dashboard" },
    { icon: AlertTriangle, label: "Errors", href: "/admin/errors" },
    { icon: Activity, label: "Prompt Activity", href: "/admin/activity" },
    { icon: SlidersHorizontal, label: "AI Models", href: "/admin/ai-models" },
  ] },
  { label: "Intelligence", items: INTELLIGENCE_ITEMS },
  { label: "Billing", items: BILLING_ITEMS },
  { label: "Data", items: DATA_ITEMS },
];

interface AdminSidebarProps {
  isCollapsed: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
}

export function AdminSidebar({ isCollapsed, onToggle, onNavigate }: AdminSidebarProps) {
  const [location] = useLocation();

  return (
    <nav aria-label="Admin navigation" className="flex h-full flex-col border-r border-[#DCE3ED] bg-[#F7F9FC] text-[#334155]">
      <div className={cn("flex h-20 shrink-0 items-center", isCollapsed ? "justify-center px-2" : "gap-3 px-5")}>
        <Link href="/" onClick={onNavigate} aria-label="Back to Admoji" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#1A1A1A] text-white">
          <img src="/admoji-logo-new.jpg" alt="" className="h-9 w-9 rounded-xl object-cover" />
        </Link>
        {!isCollapsed && <div className="min-w-0 flex-1"><span className="text-lg font-semibold tracking-tight text-[#1A1A1A]">Admoji</span><p className="text-[11px] text-[#526074]">Administration</p></div>}
        {!isCollapsed && onToggle && <button onClick={onToggle} aria-label="Collapse admin navigation" className="rounded-lg p-1.5 hover:bg-[#EAF0F7]" data-testid="button-toggle-admin-sidebar"><ChevronLeft className="h-4 w-4" /></button>}
      </div>
      {isCollapsed && onToggle && <button onClick={onToggle} aria-label="Expand admin navigation" className="mx-auto mb-3 rounded-lg p-2 hover:bg-[#EAF0F7]" data-testid="button-toggle-admin-sidebar"><ChevronRight className="h-4 w-4" /></button>}
      <div className={cn("flex-1 space-y-6 overflow-y-auto pb-5", isCollapsed ? "px-2" : "px-3")}>
        {SECTIONS.map(section => <div key={section.label}>
          {!isCollapsed && <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#5F6D82]">{section.label}</p>}
          <div className="space-y-1">
            {section.items.map(item => {
              const active = location === item.href || location.startsWith(item.href + "/");
              const classes = cn("flex min-h-10 items-center rounded-xl text-[13px] transition-colors", isCollapsed ? "justify-center px-2" : "gap-3 px-3", item.disabled ? "text-[#94A3B8]" : active ? "bg-[#EAF0F7] font-semibold text-[#1A1A1A]" : "text-[#526074] hover:bg-[#EAF0F7] hover:text-[#1A1A1A]");
              const content = <><item.icon className="h-[17px] w-[17px] shrink-0 stroke-[1.7]" />{!isCollapsed && <><span className="flex-1">{item.label}</span>{item.disabled && <span className="rounded-md border border-[#DCE3ED] px-1.5 py-0.5 text-[9px]">Soon</span>}</>}</>;
              return item.disabled ? <div key={item.href} aria-disabled="true" title={item.label + " (coming soon)"} className={classes}>{content}</div> : <Link key={item.href} href={item.href} onClick={onNavigate} aria-current={active ? "page" : undefined} title={isCollapsed ? item.label : undefined} className={classes}>{content}</Link>;
            })}
          </div>
        </div>)}
      </div>
      <div className="border-t border-[#DCE3ED] p-3">
        <Link href="/" onClick={onNavigate} className={cn("flex items-center gap-2 rounded-xl py-2.5 text-xs text-[#526074] hover:bg-[#EAF0F7]", isCollapsed ? "justify-center" : "px-3")} data-testid="link-back-to-app" title="Back to app">
          <ArrowLeft className="h-4 w-4 shrink-0" />
          {!isCollapsed && <><span>Back to app</span><span data-testid="admin-app-version" title={`Build v${APP_VERSION}`} className="ml-auto text-[10px] tabular-nums">v{APP_VERSION}</span></>}
        </Link>
      </div>
    </nav>
  );
}
